import ExitToAppIcon from "@mui/icons-material/ExitToApp";
import { Stack, Typography, List, ListItem } from "@mui/material";
import { ButtonLink } from "Components/Shared/ButtonLink";
import { PrimaryPaper } from "Components/Shared/PrimaryPaper";
import { useIsUserInMooneLite } from "Hooks/useIsUserInMooneLite";
import { type MooneLiteCta, useMooneLiteCta } from "Hooks/useMooneLiteCta";
import type { ReactNode } from "react";
import { Resources, useResource } from "Translations/Resources";

const COMPONENT_RESOURCES = Resources.Components.MooneLitePopup;

type Props = {
  isLiteVariantHidden?: boolean;
};

export const MooneLitePopup = ({ isLiteVariantHidden = false }: Props) => {
  const {
    isUserInMooneLite,
    isUserInBlockedMoonLite,
    isDocExpired,
    isLiteBecauseOfBank,
  } = useIsUserInMooneLite();
  const { t } = useResource();
  const cta = useMooneLiteCta();

  if (isUserInBlockedMoonLite) {
    return <Layout message={t(COMPONENT_RESOURCES.LimitExceeded)} cta={cta} />;
  }

  // The Lite nags (complete registration / connect a bank) can be suppressed
  // where the setup checklist owns those prompts; the doc-expired variant
  // always renders.
  if (isLiteVariantHidden && !isDocExpired) {
    return null;
  }

  if (isUserInMooneLite) {
    return (
      <Layout
        message={t(
          isLiteBecauseOfBank
            ? COMPONENT_RESOURCES.BankVerificationMessage
            : COMPONENT_RESOURCES.Message,
        )}
        cta={cta}
      >
        <List dense disablePadding>
          <ListItem sx={{ p: 0 }}>
            <Typography>
              - {t(COMPONENT_RESOURCES.RestrictionList.NoBalance)}
            </Typography>
          </ListItem>
          <ListItem sx={{ p: 0 }}>
            <Typography>
              - {t(COMPONENT_RESOURCES.RestrictionList.NoIncomingPayments)}
            </Typography>
          </ListItem>
          <ListItem sx={{ p: 0 }}>
            <Typography>
              - {t(COMPONENT_RESOURCES.RestrictionList.NoPeerToPeerPayments)}
            </Typography>
          </ListItem>
          <ListItem sx={{ p: 0 }}>
            <Typography>
              - {t(COMPONENT_RESOURCES.RestrictionList.NoLoyaltyPoints)}
            </Typography>
          </ListItem>
          <ListItem sx={{ p: 0 }}>
            <Typography>
              - {t(COMPONENT_RESOURCES.RestrictionList.MaxPaymentLimit)}
            </Typography>
          </ListItem>
          <ListItem sx={{ p: 0 }}>
            <Typography>
              - {t(COMPONENT_RESOURCES.RestrictionList.MaxTransactionsLimit)}
            </Typography>
          </ListItem>
        </List>
      </Layout>
    );
  }

  return null;
};

type LayoutProps = {
  message: string;
  cta: MooneLiteCta;
  children?: ReactNode;
};

const Layout = ({ children, message, cta }: LayoutProps) => {
  const { t } = useResource();

  return (
    <PrimaryPaper
      sx={({ palette }) => ({
        border: ["none", "none", `1px solid ${palette.divider}`],
        color: palette.text.secondary,
      })}
    >
      <Stack gap={3}>
        <Typography fontWeight={600}>{t(COMPONENT_RESOURCES.Title)}</Typography>
        <Typography>{message}</Typography>
        {children}
        <ButtonLink
          color="primary"
          variant="contained"
          fullWidth
          link={cta.link}
          text={cta.text}
          endIcon={<ExitToAppIcon />}
        />
      </Stack>
    </PrimaryPaper>
  );
};
